import i18n from '../../i18n';

import { useAuthStore } from '../../stores/authStore';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: string;
    message?: string;
    pagination?: {
        page: number;
        limit: number;
        total: number;
        pages: number;
    };
}

/**
 * Lightweight API client that auto-injects Accept-Language header
 * and Authorization token based on the current store state.
 */
async function request<T>(path: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const locale = i18n.language || 'en';
    const url = `${API_BASE}${path}`;

    // Get token from store (non-reactive)
    const token = useAuthStore.getState().token;

    const headers: Record<string, string> = {
        'Accept-Language': locale,
        'Content-Type': 'application/json',
        ...options.headers as any,
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const res = await fetch(url, {
        ...options,
        headers,
    });

    const json = await res.json();

    if (!res.ok || json.success === false) {
        throw new Error(json.error || json.message || `Request failed: ${res.status}`);
    }

    return json;
}

export const api = {
    get: <T>(path: string) => request<T>(path),
    post: <T>(path: string, body: unknown) =>
        request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
    put: <T>(path: string, body: unknown) =>
        request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
    patch: <T>(path: string, body?: unknown) =>
        request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
    delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

// Search
export const searchGlobal = (params: { q: string, locale?: string }) =>
    api.get<any>('/api/search/global?' + new URLSearchParams(params as any));

export const searchUnits = (params: any) =>
    api.get<any>('/api/search/units?' + new URLSearchParams(params));

export const getSearchMeta = () =>
    api.get<any>('/api/search/meta');

export type { ApiResponse };
